import React from 'react';
import {
  Row,
  Col,
  Button,
  Checkbox,
  Collapse,
  Divider,
  Form,
  Input,
  Modal,
  Select,
  Tag,
  Tooltip,
  Radio,
  message,
  Popconfirm,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Editor from './Editor';
import { EDITOR_TYPE, allLanguages } from '../../../../constants/constants';
import { SubscriptionContext } from '../../../../contexts/SubscriptionContext';
import { trackingVitally } from '../../../../helpers/vitally';

const FormItem = Form.Item;
const { TextArea } = Input;
const Option = Select.Option;
const { Panel } = Collapse;

const initialState = {
  emailPendingActive: true,
  emailPendingTitle: '',
  emailPendingContent: '',
  emailPendingAttachmentUrl: '',
  emailApprovedActive: true,
  emailApprovedTitle: '',
  emailApprovedContent: '',
  emailApprovedAttachmentUrl: '',
  emailApprovedLabelActive: false,
  emailApprovedLabelTitle: '',
  emailApprovedLabelContent: '',
  emailApprovedLabelAttachmentUrl: '',
  emailApprovedLabelErrorActive: false,
  emailApprovedLabelErrorTitle: '',
  emailApprovedLabelErrorContent: '',
  emailApprovedLabelErrorAttachmentUrl: '',
  emailApprovedLabelManualActive: false,
  emailApprovedLabelManualTitle: '',
  emailApprovedLabelManualContent: '',
  emailApprovedLabelManualAttachmentUrl: '',
  emailShippedActive: true,
  emailShippedTitle: '',
  emailShippedContent: '',
  emailShippedAttachmentUrl: '',
  emailReceivedActive: true,
  emailReceivedTitle: '',
  emailReceivedContent: '',
  emailReceivedAttachmentUrl: '',
  emailResolvedActive: true,
  emailResolvedTitle: '',
  emailResolvedContent: '',
  emailResolvedAttachmentUrl: '',
  emailRejectedActive: true,
  emailRejectedTitle: '',
  emailRejectedContent: '',
  emailRejectedAttachmentUrl: '',
  emailStoreCreditActive: true,
  emailStoreCreditTitle: '',
  emailStoreCreditContent: '',
  emailStoreCreditAttachmentUrl: '',
  defaultLocale: false,
  emailPendingContentHtml: '',
  emailApprovedContentHtml: '',
  emailShippedContentHtml: '',
  emailReceivedContentHtml: '',
  emailResolvedContentHtml: '',
  emailRejectedContentHtml: '',
  emailApprovedLabelContentHtml: '',
  emailApprovedLabelErrorContentHtml: '',
  emailApprovedLabelManualContentHtml: '',
  emailStoreCreditContentHtml: '',
  emailPendingEditor: EDITOR_TYPE.WYSIWYG,
  emailApprovedEditor: EDITOR_TYPE.WYSIWYG,
  emailApprovedLabelEditor: EDITOR_TYPE.WYSIWYG,
  emailApprovedLabelErrorEditor: EDITOR_TYPE.WYSIWYG,
  emailApprovedLabelManualEditor: EDITOR_TYPE.WYSIWYG,
  emailShippedEditor: EDITOR_TYPE.WYSIWYG,
  emailReceivedEditor: EDITOR_TYPE.WYSIWYG,
  emailResolvedEditor: EDITOR_TYPE.WYSIWYG,
  emailRejectedEditor: EDITOR_TYPE.WYSIWYG,
  emailStoreCreditEditor: EDITOR_TYPE.WYSIWYG,
  emailInTransitEditor: EDITOR_TYPE.WYSIWYG,
  emailReceivedGiftExchangeEditor: EDITOR_TYPE.WYSIWYG,
  emailInTransitActive: true,
  emailInTransitTitle: '',
  emailInTransitContent: '',
  emailInTransitAttachmentUrl: '',
  emailInTransitContentHtml: '',
  status: 'active',
};

const statusDescription = {
  Pending: (
    <React.Fragment>
      An email for a <strong>Pending Return</strong> is sent out to
      customers when they initially submit a return through the
      return-portal and wait for your approval.
    </React.Fragment>
  ),
  Approved: (
    <React.Fragment>
      An email for an <strong>Approved Return</strong> is sent out to
      customers once you approve the return in the dashboard (or
      auto-approved) and no prepaid label is enabled for this return. Make
      sure to <strong>include a return address</strong> (with country if you
      have international customers) and precise instructions for your
      customers.
    </React.Fragment>
  ),
  ApprovedLabel: (
    <React.Fragment>
      Email for an <strong>Approved Return</strong> that is sent out to
      customers instead of the regular Approved-Email once you approve the
      return in the dashboard (or auto-approved). This email will trigger if
      an automatic return label is present.
    </React.Fragment>
  ),
  ApprovedLabelError: (
    <React.Fragment>
      Email that is sent out when there is an issue creating the automatic
      label (e.g. address errors, etc). You want to tell the customer that
      you'll get back to them with a label asap to avoid additional support
      inquiries.
    </React.Fragment>
  ),
  ApprovedLabelManual: (
    <React.Fragment>
      Email for an <strong>Approved Return</strong> that is sent out to the
      customer instead of the regular Approved-Email once you approve the
      return in the dashboard. This email will trigger if a manually
      uploaded return label is present.
    </React.Fragment>
  ),
  Shipped: (
    <React.Fragment>
      An email for a <strong>Shipped Return</strong> is sent out to
      customers when the status of the return is set to shipped by either
      you or the customer.
    </React.Fragment>
  ),
  InTransit: (
    <React.Fragment>
      An email for an <strong>In Transit Return</strong> is sent out to
      customers when the status of the return is set to in transit by either
      you or the customer.
    </React.Fragment>
  ),
  Received: (
    <React.Fragment>
      An email for a <strong>Received Return</strong> is sent out to
      customers once you received the package or product.
    </React.Fragment>
  ),
  Resolved: (
    <React.Fragment>
      An email for a <strong>Resolved Return</strong> is sent out to
      customers once you issued a refund or exchange.
    </React.Fragment>
  ),
  Rejected: (
    <React.Fragment>
      An email for a <strong>Rejected Return</strong> is sent out to
      customers when you reject their return inquiry. You can include
      default reasons for a rejection in this email (like hygiene, out of
      refund period, final sale, etc). Reach out to the customer directly if
      you want to explain something you can't in this template.
    </React.Fragment>
  ),
  StoreCredit: (
    <React.Fragment>
      An email when <strong>Store Credit</strong> is issued during the
      refund process. This email contains the code of the gift-card or
      discount-code for the customer to redeem their store-credit.
    </React.Fragment>
  ),
};

class EmailMessages extends React.Component {
  static contextType = SubscriptionContext;

  constructor(props) {
    super(props);

    this.state = {
      ...initialState,
      locale: 'en',
      availableLanguages: [],
      allEmailSettings: [],
      status: 'active',
      selectedTemplate: 'Pending',
      visible: false,
      receivingEmail: '',
      emailDefaultContent: {},
      originEditor: EDITOR_TYPE.WYSIWYG,
      isDecodeOrignal: false,
      loading: true,
      imageUrl: '',
      enableHtmlEmailTemplates: false,
    };
  }

  formRef = React.createRef();

  componentDidMount = async () => {
    await this.initializeComponent();
    this.setState({ loading: false });
  };

  initializeComponent = async () => {
    try {
      await this.fetchEmailSettings();
      this.setState({
        originEditor: this.state[`email${this.state.selectedTemplate}Editor`],
      });
    } catch (err) {
      this.setState({ loading: false });
      message.error('Error loading email settings. Please try again.', 4);
    }
  };

  fetchEmailSettings = async () => {
    try {
      const response = await axios.get('/api/emailSettings');
      if (response.data.status === 'error') {
        const errorMessage = response.data.error;
        return message.error(errorMessage, 4);
      }
      const allEmailSettings = response.data.emailSettings;
      let emailData = allEmailSettings;
      let availableLanguages = [];
      allEmailSettings.map((email) => {
        availableLanguages.push(email.locale);
        if (
          email.defaultLocale === true ||
          email.locale === this.state.locale
        ) {
          emailData = email;
        }
      });
      const emailDefaultTemplate = response.data.emailDefault.reduce(
        (data, i) => {
          data[i.locale] = i;
          return data;
        },
        {}
      );
      return this.setState({
        ...emailData,
        availableLanguages: availableLanguages,
        allEmailSettings: allEmailSettings,
        emailDefaultContent: emailDefaultTemplate,
        enableHtmlEmailTemplates: response.data.enableHtmlEmailTemplates,
      });
    } catch (err) {
      return message.error(
        'There was an error loading your email templates. Try again.',
        4
      );
    }
  };

  handleSubmit = async () => {
    try {
      const formValues = await this.formRef.current.validateFields();

      let values = { ...formValues, status: this.state.status };

      if (this.state.enableHtmlEmailTemplates) {
        let editorKey = `email${this.state.selectedTemplate}Editor`;
        let contentHtmlKey = `email${this.state.selectedTemplate}ContentHtml`;

        let isHtmlEditor = this.state[editorKey] === EDITOR_TYPE.HTML_EDITOR;
        let emailContent = isHtmlEditor
          ? this.encodeHTML(this.state[contentHtmlKey])
          : this.state[contentHtmlKey] || '';

        if (emailContent === '<p><br></p>') {
          return message.error("Email content can't be empty. Try again.", 4);
        }

        values = {
          ...values,
          [contentHtmlKey]: emailContent,
          [editorKey]: this.state[editorKey] || EDITOR_TYPE.WYSIWYG,
        };
      }

      const response = await axios.post('/api/emailSettings/update', values);

      if (response.data.status === 'error') {
        return message.error(response.data.error, 4);
      }

      trackingVitally('rr_customeremail_edited');

      message.success('E-Mail settings updated successfully.', 4);

      const availableLanguages = [...this.state.availableLanguages];
      if (!availableLanguages.includes(response.data.locale)) {
        availableLanguages.push(response.data.locale);
      }

      // Update allEmailSettings with the new/updated email setting
      let updatedAllEmailSettings = [...this.state.allEmailSettings];
      const existingIndex = updatedAllEmailSettings.findIndex(
        (e) => e.locale === response.data.locale
      );
      if (existingIndex >= 0) {
        // Update existing entry
        updatedAllEmailSettings[existingIndex] = response.data;
      } else {
        // Add new entry
        updatedAllEmailSettings.push(response.data);
      }

      this.formRef.current.resetFields();

      this.setState({
        ...response.data,
        availableLanguages,
        allEmailSettings: updatedAllEmailSettings,
        originEditor: this.state[`email${this.state.selectedTemplate}Editor`],
        isDecodeOrignal: false,
      });
    } catch (err) {
      if (err.errorFields) {
        return message.error(
          'Error saving your email templates. Try again.',
          4
        );
      }

      return message.error(
        'There was an error saving your email templates. Try again.',
        4
      );
    }
  };

  handleChange = async (value) => {
    // Preserve the current locale when switching email templates
    const currentLocale = this.state.locale;
    
    this.setState({
      loading: true,
      selectedTemplate: value,
      originEditor: this.state[`email${value}Editor`],
      isDecodeOrignal: false,
    });

    // Find email data for the current locale from already loaded settings
    const emailData = this.state.allEmailSettings.find(
      (email) => email.locale === currentLocale
    );

    if (emailData) {
      this.setState({
        ...emailData,
        locale: currentLocale,
        selectedTemplate: value,
        originEditor: emailData[`email${value}Editor`] || EDITOR_TYPE.WYSIWYG,
        isDecodeOrignal: false,
        loading: false,
      });
    } else {
      this.setState({
        ...initialState,
        locale: currentLocale,
        selectedTemplate: value,
        availableLanguages: this.state.availableLanguages,
        allEmailSettings: this.state.allEmailSettings,
        emailDefaultContent: this.state.emailDefaultContent,
        enableHtmlEmailTemplates: this.state.enableHtmlEmailTemplates,
        isDecodeOrignal: false,
        loading: false,
      });
    }
  };

  handleLanguageChange = async (value) => {
    this.setState({
      locale: value,
      loading: true,
    });
    try {
      const response = await axios.get('/api/emailSettings');
      if (response.data.status === 'error') {
        const errorMessage = response.data.error;
        return message.error(errorMessage, 4);
      }

      let emailData = response.data.emailSettings.filter((email) => {
        if (email.locale === value) {
          return email;
        }
      });
      if (emailData.length > 0) {
        this.setState(
          {
            ...emailData[0],
            isDecodeOrignal: false,
            loading: false,
          },
          () => {
            this.formRef.current.setFieldsValue({
              locale: this.state.locale,
              defaultLocale: this.state.defaultLocale,
            });
          }
        );
      } else {
        this.setState(
          {
            ...initialState,
            locale: value,
            isDecodeOrignal: false,
            loading: false,
          },
          () => {
            this.formRef.current.setFieldsValue({
              locale: this.state.locale,
              defaultLocale: this.state.defaultLocale,
            });
          }
        );
      }
    } catch (err) {
      return message.error(
        'There was an error changing your email templates. Try again.',
        4
      );
    }
  };

  getLanguageTags = (languageCode) => {
    const emailSetting = this.state.allEmailSettings.find(
      (e) => e.locale === languageCode
    );
    const elements = [];

    if (emailSetting?.status === 'active') {
      elements.push(
        <Tooltip
          key="active"
          title="This language is active for customer emails."
        >
          <Tag color="green" style={{ marginLeft: 6 }}>
            Active
          </Tag>
        </Tooltip>
      );
    }

    if (emailSetting?.defaultLocale) {
      elements.push(
        <Tooltip
          key="default"
          title="This is your default language for customer emails."
        >
          <Tag color="gray" style={{ marginLeft: 6 }}>
            Default
          </Tag>
        </Tooltip>
      );
    }

    return elements;
  };

  toggleLanguageStatus = async () => {
    try {
      const currentEmailSetting = this.state.allEmailSettings.find(
        (e) => e.locale === this.state.locale
      );
      const newStatus =
        currentEmailSetting?.status === 'active' ? 'inactive' : 'active';

      const response = await axios.post('/api/emailSettings/status', {
        locale: this.state.locale,
        status: newStatus,
      });

      if (response.data.status === 'error') {
        return message.error(response.data.error, 4);
      }

      // Update allEmailSettings with new status
      const updatedAllEmailSettings = this.state.allEmailSettings.map((e) => {
        if (e.locale === this.state.locale) {
          return { ...e, status: newStatus };
        }
        return e;
      });

      this.setState({
        status: newStatus,
        allEmailSettings: updatedAllEmailSettings,
      });

      message.success(
        `Language ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully.`,
        4
      );
    } catch (err) {
      return message.error('Error updating language status. Try again.', 4);
    }
  };

  toggleDefaultLanguage = async () => {
    try {
      const response = await axios.post('/api/emailSettings/defaultLanguage', {
        locale: this.state.locale,
      });

      if (response.data.status === 'error') {
        return message.error(response.data.error, 4);
      }

      // Update allEmailSettings: set new default and remove from others
      const updatedAllEmailSettings = this.state.allEmailSettings.map((e) => {
        if (e.locale === this.state.locale) {
          return { ...e, defaultLocale: true };
        }
        return { ...e, defaultLocale: false };
      });

      this.setState({
        defaultLocale: true,
        allEmailSettings: updatedAllEmailSettings,
      });

      message.success('Default language updated successfully.', 4);
    } catch (err) {
      return message.error('Error updating default language. Try again.', 4);
    }
  };

  resetToOriginal = () => {
    const defaultData = this.state.emailDefaultContent[this.state.locale];
    if (!defaultData) {
      return message.error('No default template available for this language.', 4);
    }

    // Extract the relevant fields from the default template
    const resetFields = {
      emailPendingTitle: defaultData.emailPendingTitle || '',
      emailPendingContent: defaultData.emailPendingContent || '',
      emailPendingContentHtml: defaultData.emailPendingContentHtml || '',
      emailApprovedTitle: defaultData.emailApprovedTitle || '',
      emailApprovedContent: defaultData.emailApprovedContent || '',
      emailApprovedContentHtml: defaultData.emailApprovedContentHtml || '',
      emailApprovedLabelTitle: defaultData.emailApprovedLabelTitle || '',
      emailApprovedLabelContent: defaultData.emailApprovedLabelContent || '',
      emailApprovedLabelContentHtml: defaultData.emailApprovedLabelContentHtml || '',
      emailApprovedLabelErrorTitle: defaultData.emailApprovedLabelErrorTitle || '',
      emailApprovedLabelErrorContent: defaultData.emailApprovedLabelErrorContent || '',
      emailApprovedLabelErrorContentHtml: defaultData.emailApprovedLabelErrorContentHtml || '',
      emailApprovedLabelManualTitle: defaultData.emailApprovedLabelManualTitle || '',
      emailApprovedLabelManualContent: defaultData.emailApprovedLabelManualContent || '',
      emailApprovedLabelManualContentHtml: defaultData.emailApprovedLabelManualContentHtml || '',
      emailShippedTitle: defaultData.emailShippedTitle || '',
      emailShippedContent: defaultData.emailShippedContent || '',
      emailShippedContentHtml: defaultData.emailShippedContentHtml || '',
      emailInTransitTitle: defaultData.emailInTransitTitle || '',
      emailInTransitContent: defaultData.emailInTransitContent || '',
      emailInTransitContentHtml: defaultData.emailInTransitContentHtml || '',
      emailReceivedTitle: defaultData.emailReceivedTitle || '',
      emailReceivedContent: defaultData.emailReceivedContent || '',
      emailReceivedContentHtml: defaultData.emailReceivedContentHtml || '',
      emailResolvedTitle: defaultData.emailResolvedTitle || '',
      emailResolvedContent: defaultData.emailResolvedContent || '',
      emailResolvedContentHtml: defaultData.emailResolvedContentHtml || '',
      emailRejectedTitle: defaultData.emailRejectedTitle || '',
      emailRejectedContent: defaultData.emailRejectedContent || '',
      emailRejectedContentHtml: defaultData.emailRejectedContentHtml || '',
      emailStoreCreditTitle: defaultData.emailStoreCreditTitle || '',
      emailStoreCreditContent: defaultData.emailStoreCreditContent || '',
      emailStoreCreditContentHtml: defaultData.emailStoreCreditContentHtml || '',
      emailReceivedGiftExchangeTitle: defaultData.emailReceivedGiftExchangeTitle || '',
      emailReceivedGiftExchangeContent: defaultData.emailReceivedGiftExchangeContent || '',
      emailReceivedGiftExchangeContentHtml: defaultData.emailReceivedGiftExchangeContentHtml || '',
    };

    this.setState(resetFields, () => {
      // Update form fields
      this.formRef.current.setFieldsValue({
        [`email${this.state.selectedTemplate}Title`]: this.state[`email${this.state.selectedTemplate}Title`],
        [`email${this.state.selectedTemplate}Content`]: this.state[`email${this.state.selectedTemplate}Content`],
      });
      message.success('Email templates reset to original defaults.', 4);
    });
  };

  onChange = (e) => {
    const target = e.target.id;
    this.setState({
      [target]: e.target.checked,
    });
  };

  onChangeInput = (e) => {
    const target = e.target.id;
    this.setState({
      [target]: e.target.value,
    });
  };

  onChangeText = (target, text) => {
    if (this.state[target] !== text) {
      this.setState({
        [target]: text,
        isDecodeOrignal: true,
      });
    }
  };

  showModal = () => {
    this.setState({ visible: true });
  };

  handleCancel = () => {
    this.setState({ visible: false });
  };

  onChangeEmail = (e) => {
    this.setState({ receivingEmail: e.target.value });
  };

  encodeHTML = (s) => {
    return btoa(unescape(encodeURIComponent(s)));
  };

  sendEmail = async () => {
    try {
      const email = this.state.receivingEmail;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const test = emailRegex.test(email);
      if (!test) {
        return message.error('Please enter a valid email and try again.', 4);
      }
      const content = {
        emailTitle: this.state[`email${this.state.selectedTemplate}Title`],
        emailContent:
          this.state[`email${this.state.selectedTemplate}Editor`] &&
          this.state[`email${this.state.selectedTemplate}Editor`] ===
            EDITOR_TYPE.HTML_EDITOR
            ? this.encodeHTML(
                this.state[`email${this.state.selectedTemplate}ContentHtml`]
              )
            : this.state[`email${this.state.selectedTemplate}ContentHtml`] ||
              '',
      };
      const response = await axios.post('/api/emailSettings/sendTestEmail', {
        email,
        locale: this.state.locale,
        content,
        imageUrl: this.state.imageUrl,
        isEncode:
          this.state[`email${this.state.selectedTemplate}Editor`] ===
          EDITOR_TYPE.HTML_EDITOR,
      });
      if (response.status != 200) {
        return message.error(
          'Something went wrong while sending email, Please try again later.',
          5
        );
      }
      this.setState({ visible: false });
      return message.success('Email sent successfully!', 5);
    } catch (err) {
      console.log('err: ', err);
      message.error('Error sending email. Try again.', 4);
    }
  };

  render() {
    const formItemLayout = {
      labelCol: {
        xs: { span: 24 },
        sm: { span: 24 },
      },
      wrapperCol: {
        xs: { span: 24 },
        sm: { span: 24 },
      },
    };

    const existingLanguages = allLanguages.reduce((acc, language) => {
      if (this.state.availableLanguages.includes(language.code)) {
        acc.push(
          <Option value={language.code} key={language.code}>
            {language.name} {this.getLanguageTags(language.code)}
          </Option>
        );
      }
      return acc;
    }, []);

    const remainingLanguages = allLanguages.reduce((acc, language) => {
      if (!this.state.availableLanguages.includes(language.code)) {
        acc.push(
          <Option value={language.code} key={language.code}>
            {language.name}
          </Option>
        );
      }
      return acc;
    }, []);

    const defaultTitle = this.state.emailDefaultContent[this.state.locale]
      ? this.state.emailDefaultContent[this.state.locale][
          `email${this.state.selectedTemplate}Title`
        ]
      : '';

    const defaultContent = this.state.emailDefaultContent[this.state.locale]
      ? this.state.emailDefaultContent[this.state.locale][
          `email${this.state.selectedTemplate}Content`
        ]
      : '';

    const defaultContentHtml = this.state.emailDefaultContent[this.state.locale]
      ? this.state.emailDefaultContent[this.state.locale][
          `email${this.state.selectedTemplate}ContentHtml`
        ]
      : '';

    const valueContentHtml =
      this.state[`email${this.state.selectedTemplate}ContentHtml`] &&
      this.state[`email${this.state.selectedTemplate}ContentHtml`].length > 0
        ? this.state[`email${this.state.selectedTemplate}ContentHtml`]
        : defaultContentHtml;

    const { enableHtmlEmailTemplates } = this.state;

    if (this.state.loading) {
      return (
        <div style={{ position: 'absolute', top: '50%', left: '50%' }}>
          <div className="GlobalLoader GlobalLoader--md"></div>
        </div>
      );
    }

    return (
      <Form
        onFinish={this.handleSubmit}
        hideRequiredMark={true}
        ref={this.formRef}
      >
        <div
          className="flex-row flex-row--noMargin flex-top-xxs flex-between-xxs"
          style={{ gap: 20, width: '100%' }}
        >
          <div
            className="flex-col-xxs-12"
            style={{ padding: 0, width: '100%' }}
          >
            <div className="ContentPanel">
              <div className="flex-row u-rowGap--sm">
                <div className="flex-col-xxs-12">
                  <div
                    className="flex-row flex-row--noMargin"
                    style={{ width: '100%' }}
                  >
                    <h4 data-intercom-target="customer-emails-language-heading-notifications-customer-emails">
                      Select translation language
                    </h4>
                    <div
                      className="flex-row flex-row--noMargin"
                      style={{ width: '100%', gap: 10 }}
                    >
                      <FormItem
                        {...formItemLayout}
                        name="locale"
                        initialValue={this.state.locale}
                      >
                        <Select
                          style={{ width: '100%' }}
                          onChange={this.handleLanguageChange}
                          placeholder="Select language"
                          data-intercom-target="customer-emails-language-dropdown-notifications-customer-emails"
                        >
                          <Option key="disabledExisting" disabled>
                            --- Existing Languages
                          </Option>
                          {existingLanguages}
                          <Option key="disabledRemaining" disabled>
                            --- Available Languages
                          </Option>
                          {remainingLanguages}
                        </Select>
                      </FormItem>
                    </div>
                    {this.state.availableLanguages.includes(this.state.locale) && (
                      <div
                        className="flex-row flex-row--noMargin flex-middle-xxs"
                        style={{ gap: 10, marginTop: 10 }}
                      >
                        <Button
                          onClick={this.toggleLanguageStatus}
                          style={{
                            color:
                              this.state.status === 'active'
                                ? '#da121d'
                                : '#2b7e01',
                            borderColor:
                              this.state.status === 'active'
                                ? '#f0827c'
                                : '#5ccb07',
                            backgroundColor:
                              this.state.status === 'active'
                                ? '#fff0f0'
                                : '#f4feec',
                          }}
                          data-intercom-target="customer-emails-toggle-status-button-notifications-customer-emails"
                        >
                          {this.state.status === 'active'
                            ? 'Deactivate this language'
                            : 'Activate this language'}
                        </Button>
                        <Tooltip
                          placement="bottom"
                          title="When active, emails will be sent in this language to matching customers."
                        >
                          <InfoCircleOutlined />
                        </Tooltip>
                        <Button
                          onClick={this.toggleDefaultLanguage}
                          disabled={this.state.defaultLocale}
                          style={{
                            marginLeft: 10,
                            backgroundColor: this.state.defaultLocale
                              ? undefined
                              : 'gray',
                            color: this.state.defaultLocale ? undefined : 'white',
                          }}
                          data-intercom-target="customer-emails-toggle-default-button-notifications-customer-emails"
                        >
                          {this.state.defaultLocale
                            ? 'This is the default language'
                            : 'Make this the default language'}
                        </Button>
                        <Tooltip
                          placement="bottom"
                          title="The default language will be used when the customer's language is not recognized."
                        >
                          <InfoCircleOutlined />
                        </Tooltip>
                        <Popconfirm
                          title="Reset to original"
                          description="Are you sure you want to reset all email templates for this language to the original defaults? This will overwrite your current content."
                          onConfirm={this.resetToOriginal}
                          okText="Yes, reset"
                          cancelText="Cancel"
                          disabled={!this.state.emailDefaultContent[this.state.locale]}
                        >
                          <Button
                            disabled={!this.state.emailDefaultContent[this.state.locale]}
                            style={{ marginLeft: 10 }}
                            data-intercom-target="customer-emails-reset-to-original-button-notifications-customer-emails"
                          >
                            Reset to original
                          </Button>
                        </Popconfirm>
                        <Tooltip
                          placement="bottom"
                          title="Reset all email templates for this language to the original default values."
                        >
                          <InfoCircleOutlined />
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className="flex-row flex-row--noMargin flex-top-xxs flex-between-xxs"
          style={{ gap: 20, width: '100%' }}
        >
          <div
            className="flex-col-xxs-12"
            style={{ padding: 0, width: '100%' }}
          >
            <div className="ContentPanel">
              <div className="flex-row u-rowGap--sm">
                <div className="flex-col-xxs-12 flex-col-sm-6">
                  <h4 data-intercom-target="customer-emails-email-type-heading-notifications-customer-emails">
                    Email type
                  </h4>
                  <div
                    className="flex-row flex-row--noMargin"
                    style={{ width: '100%', gap: 10 }}
                  >
                    <FormItem
                      {...formItemLayout}
                      name="selectedTemplate"
                      initialValue={this.state.selectedTemplate}
                    >
                      <Select
                        style={{ width: '100%' }}
                        onChange={this.handleChange}
                        placeholder="Select template"
                        data-intercom-target="customer-emails-email-type-dropdown-notifications-customer-emails"
                      >
                        <Option value="Pending">Pending Return</Option>
                        <Option value="Approved">Approved Return</Option>
                        <Option value="ApprovedLabel">
                          Approved Return (Automatic Prepaid Label)
                        </Option>
                        <Option value="ApprovedLabelError">
                          Approved Return (Automatic Prepaid Label - Error)
                        </Option>
                        <Option value="ApprovedLabelManual">
                          Approved Return (Manually Uploaded Prepaid Label)
                        </Option>
                        <Option value="Shipped">Shipped Return</Option>
                        <Option value="InTransit">In Transit Return</Option>
                        <Option value="Received">Received Return</Option>
                        <Option value="Resolved">Resolved Return</Option>
                        <Option value="Rejected">Rejected Return</Option>
                        {/* <Option value="ReceivedGiftExchange">
                          Received Gift Exchange
                        </Option> */}
                        <Option value="StoreCredit">Store-Credit</Option>
                      </Select>
                    </FormItem>
                    <div>{statusDescription[this.state.selectedTemplate]}</div>
                  </div>
                </div>
                <div className="flex-col-xxs-12 flex-col-sm-6" style={{ paddingLeft: 20 }}>
                  <h4 data-intercom-target="customer-emails-send-to-customer-heading-notifications-customer-emails">
                    Send to customer?{' '}
                    <Tooltip
                      placement="bottom"
                      title="Enable this option to send the email template to your customers"
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  </h4>
                  <div
                    className="flex-row flex-row--noMargin"
                    style={{ width: '100%' }}
                  >
                    <Form.Item
                      {...formItemLayout}
                      name={`email${this.state.selectedTemplate}Active`}
                      valuePropName="checked"
                      initialValue={this.state[`email${this.state.selectedTemplate}Active`]}
                    >
                      <Checkbox
                        onChange={this.onChange}
                        data-intercom-target="customer-emails-send-to-customer-checkbox-notifications-customer-emails"
                      >
                        Send email to customer
                      </Checkbox>
                    </Form.Item>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Divider />
        <div
          className="flex-row flex-row--noMargin flex-top-xxs flex-between-xxs"
          style={{ gap: 20, width: '100%' }}
        >
          <div
            className="flex-col-xxs-12"
            style={{ padding: 0, width: '100%' }}
          >
            <div className="ContentPanel">
              <div className="u-marginBottom--md">
                <h4 data-intercom-target="customer-emails-email-subject-heading-notifications-customer-emails">
                  Email subject
                </h4>
                <p>
                  Specify the subject line for your selected email template.
                </p>
                <div
                  className="flex-row flex-row--noMargin"
                  style={{ width: '100%' }}
                >
                  <FormItem
                    {...formItemLayout}
                    name={`email${this.state.selectedTemplate}Title`}
                    initialValue={this.state[`email${this.state.selectedTemplate}Title`] || defaultTitle}
                    rules={[
                      {
                        required: true,
                        message: 'Please enter a subject for this email.',
                      },
                    ]}
                  >
                    <Input
                      onChange={this.onChangeInput}
                      placeholder="Subject line (title) of your email."
                      data-intercom-target="customer-emails-email-subject-input-notifications-customer-emails"
                    />
                  </FormItem>
                </div>
              </div>

              <Row>
                <Col span={18}>
                  <h4
                    data-intercom-target="customer-emails-email-content-heading-notifications-customer-emails"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                    }}
                  >
                    Email content
                    <Tooltip
                      placement="bottom"
                      title={
                        enableHtmlEmailTemplates
                          ? 'Great news! Our support now extends to text, Emojis, URLs, and HTML elements. Feel free to utilize all of these in your content!'
                          : 'We currently support text, Emojis and URLs. Do not add HTML elements.'
                      }
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  </h4>
                  <p>
                    Enter the content for your email that will be sent to your
                    customers.
                  </p>
                </Col>
                {enableHtmlEmailTemplates && (
                  <Col span={6} style={{ textAlign: 'right', padding: 0 }}>
                    <Radio.Group
                      defaultValue={this.state[`email${this.state.selectedTemplate}Editor`] || EDITOR_TYPE.WYSIWYG}
                      value={this.state[`email${this.state.selectedTemplate}Editor`] || EDITOR_TYPE.WYSIWYG}
                      size="small"
                      onChange={(e) => {
                        this.onChangeText(
                          `email${this.state.selectedTemplate}Editor`,
                          e.target.value
                        );
                      }}
                      style={{ padding: 0 }}
                    >
                      <Radio.Button
                        value={EDITOR_TYPE.WYSIWYG}
                        style={{ height: 'auto', borderRadius: 0 }}
                      >
                        Text Editor
                      </Radio.Button>
                      <Radio.Button
                        value={EDITOR_TYPE.HTML_EDITOR}
                        style={{ height: 'auto', borderRadius: 0 }}
                      >
                        HTML
                      </Radio.Button>
                    </Radio.Group>
                  </Col>
                )}
              </Row>
              <div
                className="flex-row flex-row--noMargin"
                style={{ width: '100%', gap: 20 }}
              >
                <FormItem {...formItemLayout}>
                  {enableHtmlEmailTemplates &&
                    (!this.state[`email${this.state.selectedTemplate}Editor`] ||
                      this.state[`email${this.state.selectedTemplate}Editor`] === EDITOR_TYPE.WYSIWYG) && (
                      <ReactQuill
                        theme="snow"
                        value={valueContentHtml}
                        onChange={(value) => {
                          this.onChangeText(
                            `email${this.state.selectedTemplate}ContentHtml`,
                            value
                          );
                        }}
                        data-intercom-target="customer-emails-email-content-html-input-notifications-customer-emails"
                      />
                    )}
                  {enableHtmlEmailTemplates &&
                    this.state[`email${this.state.selectedTemplate}Editor`] &&
                    this.state[`email${this.state.selectedTemplate}Editor`] === EDITOR_TYPE.HTML_EDITOR && (
                      <Editor
                        content={valueContentHtml}
                        onChange={(value) => {
                          this.onChangeText(
                            `email${this.state.selectedTemplate}ContentHtml`,
                            value
                          );
                        }}
                      />
                    )}
                  {!enableHtmlEmailTemplates && (
                    <Form.Item
                      name={`email${this.state.selectedTemplate}Content`}
                      initialValue={this.state[`email${this.state.selectedTemplate}Content`] || defaultContent}
                      rules={[
                        {
                          required: true,
                          message: 'Please enter content for this email.',
                        },
                      ]}
                    >
                      <TextArea
                        onChange={this.onChangeInput}
                        placeholder="Content of your email."
                        autoSize={{ minRows: 4 }}
                        data-intercom-target="customer-emails-email-content-text-input-notifications-customer-emails"
                      />
                    </Form.Item>
                  )}
                </FormItem>
                {enableHtmlEmailTemplates && (
                  <div
                    className="flex-row flex-row--noMargin flex-end-xxs"
                    style={{ width: '100%' }}
                  >
                    <React.Fragment>
                      <Button
                        style={{
                          borderColor: '#67C2C2',
                          color: '#67C2C2',
                          float: 'right',
                        }}
                        onClick={this.showModal}
                        data-intercom-target="customer-emails-send-test-email-button-notifications-customer-emails"
                      >
                        Send test email
                      </Button>
                      <Modal
                        title="Send Test Email"
                        visible={this.state.visible}
                        onOk={this.sendEmail}
                        onCancel={this.handleCancel}
                      >
                        <h3 data-intercom-target="customer-emails-test-email-modal-heading-notifications-customer-emails">
                          Please enter receiving email
                        </h3>
                        <Input
                          type="email"
                          placeholder="Please enter email"
                          onChange={this.onChangeEmail}
                          data-intercom-target="customer-emails-test-email-modal-email-input-notifications-customer-emails"
                        />
                      </Modal>
                    </React.Fragment>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <Divider />
        <div
          className="flex-row flex-row--noMargin flex-top-xxs flex-between-xxs"
          style={{ gap: 20, width: '100%' }}
        >
          <div
            className="flex-col-md-3 flex-col-sm-12"
            style={{ padding: 0, width: '100%' }}
          >
            <h4 data-intercom-target="customer-emails-email-personalization-heading-notifications-customer-emails">
              Email personalization
            </h4>
            <span>Use variables to effortlessly customize your emails.</span>
          </div>
          <div
            className="flex-col-md-8 flex-col-sm-12"
            style={{ padding: 0, width: '100%' }}
          >
            <div className="ContentPanel">
              <div
                className="flex-row flex-row--noMargin"
                style={{ width: '100%' }}
              >
                <FormItem {...formItemLayout}>
                  <div>
                    <Collapse>
                      <Panel
                        header="Want to personalize your emails with variables?"
                        key="1"
                        data-intercom-target="customer-emails-email-personalization-panel-notifications-customer-emails"
                      >
                        <p>
                          You can use variables to dynamically personalize your
                          emails.
                        </p>
                        <p>
                          <strong>
                            IMPORTANT: copy the variables you want to use
                            including the curly-braces to your email. They need
                            to match exactly.
                          </strong>
                        </p>
                        <p>
                          <p>The following variables are available:</p>
                          <p>
                            <strong>Order related</strong>
                          </p>
                          <p>
                            <Tag>{'{RMA_NUMBER}'}</Tag> the number of the return
                            (also called RMA number)
                          </p>
                          <p>
                            <Tag>{'{ORDER_NAME}'}</Tag> the order-name/number on
                            Shopify, e.g. #1234
                          </p>
                          <p>
                            <Tag>{'{ORDER_ID}'}</Tag> the orderId on Shopify,
                            e.g. 123456
                          </p>
                          <p>
                            <Tag>{'{ITEM_DETAILS}'}</Tag> list of items with
                            reason for return
                          </p>
                          <p>
                            <Tag>{'{REJECTION_OBSERVATION}'}</Tag> Observation
                            of rejected return
                          </p>
                          <p>
                            <strong>Business related</strong>
                          </p>
                          <p>
                            <Tag>{'{RETURN_ADDRESS}'}</Tag> the general return
                            address you have set in the Return-Settings (without
                            the phone number) or a custom address when using our
                            rules-feature
                          </p>
                          <p>
                            <strong>Customer related</strong>
                          </p>
                          <p>
                            <Tag>{'{CUSTOMER_EMAIL}'}</Tag> the email address
                            the customer used for the order
                          </p>
                          <p>
                            <Tag>{'{CUSTOMER_FIRST_NAME}'}</Tag> the first name
                            of the customer
                          </p>
                          <p>
                            <Tag>{'{CUSTOMER_LAST_NAME}'}</Tag> the last name of
                            the customer
                          </p>
                          <p>
                            <strong>Customer shipping address related</strong>
                          </p>
                          <p>
                            <Tag>{'{CUSTOMER_ADDRESS}'}</Tag> the address of the
                            customer
                          </p>
                          <p>
                            <Tag>{'{CUSTOMER_APARTMENT_SUIT}'}</Tag> the
                            apartment, suit, etc of the customer
                          </p>
                          <p>
                            <Tag>{'{CUSTOMER_CITY}'}</Tag> the city of the
                            customer
                          </p>
                          <p>
                            <Tag>{'{CUSTOMER_STATE}'}</Tag> the state of the
                            customer
                          </p>
                          <p>
                            <Tag>{'{CUSTOMER_ZIP_CODE}'}</Tag> the ZIP code of
                            the customer
                          </p>
                          <p>
                            <Tag>{'{CUSTOMER_COUNTRY}'}</Tag> the country of the
                            customer
                          </p>
                          <p>
                            <strong>Shipping-label related</strong>
                          </p>
                          <p>
                            <Tag>{'{LABEL_DOWNLOAD_LINK}'}</Tag> the download
                            link to the PDF prepaid label, only available if you
                            have automatic prepaid labels enabled
                          </p>
                          <p>
                            <Tag>{'{LABEL_QR_CODE_DOWNLOAD_LINK}'}</Tag> the
                            download link to the QR Code label. Only available
                            for USPS on EasyPost and if you have this feature
                            available on your plan
                          </p>
                          <p>
                            <Tag>{'{LABEL_TRACKING_LINK}'}</Tag> the tracking
                            link for the prepaid label, only available if you
                            have automatic prepaid labels enabled
                          </p>
                          <p>
                            <Tag>{'{COMMERCIAL_INVOICE}'}</Tag> the download
                            link to the commercial invoice for international
                            shipments (if available)
                          </p>
                          <p>
                            <Tag>{'{MANUAL_LABEL_DOWNLOAD_LINK}'}</Tag> the
                            download link to the label, only available if you
                            uploaded the prepaid-label for the return
                          </p>
                          <p>
                            <Tag>{'{MANUAL_LABEL_SECOND_DOWNLOAD_LINK}'}</Tag>{' '}
                            the download link to the second document/label, only
                            available if you uploaded the file in the dashboard
                          </p>
                          <p>
                            <strong>Refund related</strong>
                          </p>
                          <p>
                            <Tag>{'{STORE_CREDIT_CODE}'}</Tag> The code of the
                            gift-card (only Shopify Plus) or discount-code for
                            the customer to redeem their store-credit.
                          </p>
                          <p>
                            <Tag>{'{STORE_CREDIT_AMOUNT}'}</Tag> The amount of
                            store-credit in the form of gift-card (only
                            Shopify Plus) or discount-code.
                          </p>
                          <p>
                            <Tag>{'{STORE_CREDIT_CURRENCY}'}</Tag> The currency
                            of store-credit including in the form of gift-card
                            (only Shopify Plus) or discount-code.
                          </p>
                          {/* RICH-1900 */}
                          <p>
                            <Tag>{'{LABEL_FEE}'}</Tag> The label fee applied to
                            this return, if any.
                          </p>
                          <p>
                            <Tag>{'{RESTOCKING_FEE}'}</Tag> The restocking fee
                            applied to this return. Shown as a percentage when
                            the fee is configured as one.
                          </p>
                          <p>
                            <Tag>{'{CUSTOM_FEE}'}</Tag> The custom fee applied by
                            an advanced rule, if any. Shown as a percentage when
                            the fee is configured as one.
                          </p>
                          <p>
                            <Tag>{'{CUSTOM_FEE_DESCRIPTION}'}</Tag> The
                            description you entered for that custom fee.
                          </p>
                        </p>
                      </Panel>
                    </Collapse>
                  </div>
                </FormItem>
              </div>
            </div>
          </div>
        </div>
        <div
          className="flex-row flex-row--noMargin flex-end-xxs u-marginBottom--lg"
          style={{ width: '100%', marginTop: 30 }}
        >
          <Button
            type="primary"
            onClick={this.handleSubmit}
            style={{ padding: 'var(--input-padding)' }}
            data-intercom-target="customer-emails-footer-save-changes-button-notifications-customer-emails"
          >
            Save changes
          </Button>
        </div>
      </Form>
    );
  }
}

export default EmailMessages;
